// @flow
import { useContext, useMemo } from 'react';
import { useLocalize } from '@ta/platform.i18n';
import { Environment } from '@ta/lithium.context';
import type { ExplicitPreferencesState } from '@ta/onboarding.explicit-preferences';

/**
 * A rendered description of the given date range
 */
const useDatesDescription = ({
  startDate,
  endDate,
  startMonth,
  endMonth,
  flexibleDates,
}: {|
  startDate: ?Date,
  endDate: ?Date,
  startMonth: ?Date,
  endMonth: ?Date,
  flexibleDates: ?boolean,
|}) => {
  const { locale } = useContext(Environment);
  const { localize } = useLocalize();

  const monthFormat = useMemo(() => new Intl.DateTimeFormat(locale.code, { month: 'long' }), [
    locale.code,
  ]);

  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(locale.code, { month: 'short', day: 'numeric' }),
    [locale.code],
  );

  if (flexibleDates && startMonth != null && endMonth != null) {
    // If the user selected exactly one month, show that month
    if (
      startMonth.getFullYear() === endMonth.getFullYear() &&
      startMonth.getMonth() === endMonth.getMonth()
    ) {
      // Traveling in {month}
      return localize.string('cx_explicit_preferences_cta_month2', {
        month: monthFormat.format(startMonth),
      });
    }

    // Traveling from {month1} to {month2}
    return localize.string('cx_explicit_preferences_cta_months2', {
      month1: monthFormat.format(startMonth),
      month2: monthFormat.format(endMonth),
    });
  }

  if (!flexibleDates && startDate != null && endDate != null) {
    // If the user selected exactly one day, show that day
    if (
      startDate.getFullYear() === endDate.getFullYear() &&
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getDate() === endDate.getDate()
    ) {
      // Traveling on {date}
      return localize.string('cx_explicit_preferences_cta_date2', {
        date: dateFormat.format(startDate),
      });
    }

    // Traveling from {date1} to {date2}
    return localize.string('cx_explicit_preferences_cta_dates2', {
      date1: dateFormat.format(startDate),
      date2: dateFormat.format(endDate),
    });
  }

  return null;
};

/**
 * A rendered description of the given traveling companion selection
 * @param isFirstItem true if this is the first item in the bulleted list, and therefore should
 * begin with "Traveling with..."
 */
const useSegmentDescription = (
  segment: ?('FAMILY' | 'FRIENDS' | 'PARTNER' | 'SOLO'),
  isFirstItem: boolean,
) => {
  const { localize } = useLocalize();

  if (segment == null) {
    return null;
  }

  switch (segment) {
    case 'PARTNER':
      return isFirstItem
        ? // Traveling with your partner
          localize.string('cx_explicit_preferences_cta_traveling_partner')
        : // With your partner
          localize.string('cx_explicit_preferences_cta_partner2');

    case 'FAMILY':
      return isFirstItem
        ? // Traveling with family
          localize.string('cx_explicit_preferences_cta_traveling_family')
        : // With family
          localize.string('cx_explicit_preferences_cta_family2');

    case 'FRIENDS':
      return isFirstItem
        ? // Traveling with friends
          localize.string('cx_explicit_preferences_cta_traveling_friends')
        : // With friends
          localize.string('cx_explicit_preferences_cta_friends3');

    case 'SOLO':
      return isFirstItem
        ? // Traveling solo
          localize.string('cx_explicit_preferences_cta_traveling_solo')
        : // Solo
          localize.string('cx_explicit_preferences_cta_solo2');

    default:
      throw new Error(`Invalid segment: ${(segment: empty)}`);
  }
};

/**
 * @return a rendered description of the user's selected interests
 */
const useInterestsDescription = (interestOptions, interests) => {
  const { localize } = useLocalize();

  const selectedInterestOptions = [];
  interestOptions.forEach(({ value, label }) => {
    if (interests.includes(value)) {
      selectedInterestOptions.push(label);
    }
  });

  if (selectedInterestOptions.length === 0) {
    return null;
  }

  return [
    // Interested in
    `${localize.string('cx_explicit_preferences_cta_interested_in')} ${selectedInterestOptions[0]}`,
    ...selectedInterestOptions.slice(1),
  ];
};

/**
 * @return a string representing the user's current personalization choices, or null if the user
 * has not submitted any trip preferences
 */
const useExplicitPreferencesDescription = ({
  interestOptions,
  responses: { startDate, endDate, startMonth, endMonth, flexibleDates, segment, interests },
}: ExplicitPreferencesState) => {
  const datesDescription = useDatesDescription({
    startDate,
    endDate,
    startMonth,
    endMonth,
    flexibleDates,
  });

  const segmentDescription = useSegmentDescription(segment, datesDescription == null);

  const interestsDescription = useInterestsDescription(interestOptions, interests);

  const items = [];

  if (datesDescription != null) {
    items.push(datesDescription);
  }

  if (segmentDescription != null) {
    items.push(segmentDescription);
  }

  if (interestsDescription != null) {
    items.push(...interestsDescription);
  }

  if (items.length === 0) {
    return null;
  }

  // To avoid localization issues building a grammatically correct sentence, just list all of the
  // items separated by bullet characters.
  return items.join(' \u00B7 ');
};

export default useExplicitPreferencesDescription;
